/**
 * 管理画面の Firebase クライアント(App / Auth / Firestore / Functions)。
 *
 * apps/web の `services/firebase/client.ts` と同じ流儀:
 *   - `initializeApp` は1回だけ、env はここ以外から読まない
 *   - エミュレータ接続は env の1フラグで分岐
 *   - App Check は**サイトキーがあり、かつクラウド接続のときだけ**有効にする(docs/20 §3)
 * 違いは「匿名サインインをしない」点(管理画面は必ず明示ログイン)。
 * 書込み(下書き・公開・権限)はすべて管理用 Functions 経由(docs/20 §1)。
 */
import { initializeApp, type FirebaseApp } from 'firebase/app';
import { initializeAppCheck, ReCaptchaEnterpriseProvider } from 'firebase/app-check';
import {
  browserLocalPersistence,
  connectAuthEmulator,
  initializeAuth,
  type Auth,
} from 'firebase/auth';
import { connectFirestoreEmulator, getFirestore, type Firestore } from 'firebase/firestore';
import { connectFunctionsEmulator, getFunctions, type Functions } from 'firebase/functions';
import type { AdminEnv } from './env';

export interface AdminFirebase {
  readonly app: FirebaseApp;
  readonly auth: Auth;
  readonly db: Firestore;
  readonly functions: Functions;
}

function setUpAppCheck(app: FirebaseApp, env: AdminEnv): void {
  if (env.appCheckSiteKey === undefined || env.emulator !== null) return;
  if (env.appCheckDebugToken !== undefined) {
    (globalThis as { FIREBASE_APPCHECK_DEBUG_TOKEN?: string }).FIREBASE_APPCHECK_DEBUG_TOKEN =
      env.appCheckDebugToken;
  }
  initializeAppCheck(app, {
    provider: new ReCaptchaEnterpriseProvider(env.appCheckSiteKey),
    isTokenAutoRefreshEnabled: true,
  });
}

export function createAdminFirebase(env: AdminEnv, name?: string): AdminFirebase {
  const app = initializeApp(
    {
      apiKey: env.apiKey,
      authDomain: env.authDomain,
      projectId: env.projectId,
      appId: env.appId,
    },
    name,
  );
  setUpAppCheck(app, env);
  // 永続化は **localStorage に固定** する(既定の IndexedDB を使わない)。
  // 既定の indexedDBLocalPersistence は、タブが非表示のあいだ Chrome に
  // データベースを閉じられることがあり(「Database is closing/hidden」)、
  // その状態でログインすると原因の分かりにくい失敗になる。
  // 管理画面はセッションを1タブで使うだけなので localStorage で十分。
  const auth = initializeAuth(app, { persistence: browserLocalPersistence });
  const db = getFirestore(app);
  const functions = getFunctions(app, env.functionsRegion);

  if (env.emulator) {
    const { host, authPort, firestorePort, functionsPort } = env.emulator;
    connectAuthEmulator(auth, `http://${host}:${authPort}`, { disableWarnings: true });
    connectFirestoreEmulator(db, host, firestorePort);
    connectFunctionsEmulator(functions, host, functionsPort);
  }

  return { app, auth, db, functions };
}
